import {
  type ReactNode,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { ToastContext } from "../contexts/toastContext";
import type { ToastType, ToastWithTimer } from "../types/toast";
import { ToastPanel } from "../components/ui/toast/toastPanel";
import { toastManager } from "../utils/toastManager";
import { TOAST_LIFE_DURATION, TOAST_QUEUE_DELAY } from "../config/toastConfig";

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastWithTimer[]>([]);
  const [toastQueue, setToastQueue] = useState<
    Array<{ message: string; type: ToastType }>
  >([]);
  const idCounterRef = useRef(0);
  const processingTimeoutRef = useRef<number | null>(null);

  const addToastImmediate = useCallback((message: string, type: ToastType) => {
    const id = `toast-${Date.now()}-${idCounterRef.current++}`;
    const newToast: ToastWithTimer = {
      id,
      type,
      message,
      duration: TOAST_LIFE_DURATION,
      isPaused: false,
      isAppearing: true,
    };

    setToasts((prev) => [...prev, newToast]);
  }, []);

  const addToast = useCallback((message: string, type: ToastType) => {
    setToastQueue((prev) => [...prev, { message, type }]);
  }, []);

  // Обработка очереди тостов с задержкой
  useEffect(() => {
    if (toastQueue.length === 0) {
      return;
    }

    if (processingTimeoutRef.current) {
      return;
    }

    const [nextToast, ...rest] = toastQueue;
    addToastImmediate(nextToast.message, nextToast.type);
    setToastQueue(rest);

    processingTimeoutRef.current = window.setTimeout(() => {
      processingTimeoutRef.current = null;
    }, TOAST_QUEUE_DELAY);

    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    };
  }, [toastQueue, addToastImmediate]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const pauseToast = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((toast) => {
        if (toast.id === id && !toast.isPaused) {
          return { ...toast, isPaused: true };
        }
        return toast;
      }),
    );
  }, []);

  const resumeToast = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((toast) => {
        if (toast.id === id && toast.isPaused) {
          return { ...toast, isPaused: false };
        }
        return toast;
      }),
    );
  }, []);

  const handleAppearComplete = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((toast) => {
        if (toast.id === id && toast.isAppearing) {
          return { ...toast, isAppearing: false };
        }
        return toast;
      }),
    );
  }, []);

  // Регистрируем функцию в глобальном менеджере для использования вне React
  useEffect(() => {
    toastManager.register(addToast);
    return () => {
      toastManager.unregister();
    };
  }, [addToast]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastPanel
        toasts={toasts}
        onRemove={removeToast}
        onPause={pauseToast}
        onResume={resumeToast}
        onAppearComplete={handleAppearComplete}
      />
    </ToastContext.Provider>
  );
}
